<script lang="ts">
import { computed, defineComponent, Fragment, h, mergeProps, nextTick, ref, shallowRef, unref, watch } from 'vue';

import { classNames, deepEqual, IN_BROWSER, keyNames, matchesSelector, noop } from '@components/utils';

import {
  makeDisabledProps,
  makeFieldProps,
  makeFilterProps,
  makeFocusProps,
  makeIconEvents,
  makeIconProps,
  makeInputProps,
  makeItemsProps,
  makeLazyProps,
  makeLoadingProps,
  makeLocaleProps,
  makeMenuProps,
  makeReadonlyProps,
  makeSelectProps,
  makeSizeProps,
  makeThemeProps,
  makeTransitionProps,
  makeValidationProps,
  useFilter,
  useFilteredSelectProps,
  useFocus,
  useForm,
  useItems,
  useLocale,
  useMenu,
  useProxyModel,
  useRender,
  useSelect,
  useScrolling
} from '../../composable';
import { CdsCheckbox } from '../CdsCheckbox';
import { CdsHighlight } from '../CdsCombobox/composable';
import { CdsIcon } from '../CdsIcon';
import { CdsList, CdsListItem } from '../CdsList';
import { CdsMenu } from '../CdsMenu';
import { CdsTextInput } from '../CdsTextInput';
import { CdsDivider } from '../CdsDivider';

import type { IInternalItem, IItemSlotProps } from '../../composable';
import type { PropType } from 'vue';

export const componentName = 'CdsAutocomplete';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Текстовое значение поиска для фильтрации данные
     */
    search: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * При поиске будет сразу выбирать первый найденный вариант
     */
    autoSelectFirst: {
      type: [Boolean, String] as PropType<boolean | 'exact'>,
      default: undefined
    },

    ...makeSelectProps(),
    ...makeFilterProps({
      filterKeys: ['title']
    }),
    ...makeFieldProps({
      role: 'combobox'
    }),
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeLocaleProps(),
    ...makeIconProps(),
    ...makeIconEvents(),
    ...makeLazyProps(),
    ...makeLoadingProps(),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeSizeProps(),
    ...makeMenuProps(),
    ...makeItemsProps({
      itemChildren: false
    }),
    ...makeTransitionProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при фокусе
     * @type { boolean }
     */
    'update:focused': (value: boolean) => true,
    /**
     * Событие при изменении значения (v-model)
     * @type { any }
     */
    'update:modelValue': (value: any) => true,
    /**
     * Событие открытия меню
     * @type { boolean }
     */
    'update:menu': (value: boolean) => true,
    /**
     * Событие при вводе значения в поле
     * @type { string }
     */
    'update:search': (value: string) => true,
    /**
     * Событие выбора всех элементов
     * @type { boolean }
     */
    'update:selectedAll': (value: boolean) => true
  },
  setup(props, { slots, emit, expose }) {
    const { dropdown: autocompleteLocale } = useLocale(props);
    const { isFocused } = useFocus(props, componentName);
    const { internalMenu, menuRef } = useMenu<typeof CdsMenu>(props);
    const { items, transformIn, transformOut } = useItems(props);
    const { model, selected, selectStrategy, selectedAll, selectedIndeterminate } = useSelect(
      props,
      items,
      transformIn,
      transformOut
    );
    const search = useProxyModel(props, 'search', '');
    const form = useForm();

    const isPure = shallowRef(true);
    const listHasFocus = shallowRef(false);
    const selectionIndex = shallowRef(-1);

    const inputRef = ref<typeof CdsTextInput>();
    const listRef = ref<typeof CdsList>();

    const { onListKeydown, onListScroll } = useScrolling(listRef, inputRef);
    const { filteredItems, getMatches } = useFilter(props, items, () => (unref(isPure) ? '' : unref(search)));

    const displayItems = computed(() => {
      if (props.hideSelected) {
        return unref(filteredItems).filter(
          (filteredItem) => !unref(model).some((selection) => selection.value === filteredItem.value)
        );
      }

      return unref(filteredItems);
    });

    const highlightFirst = computed(() => {
      const selectFirst =
        props.autoSelectFirst === true ||
        (props.autoSelectFirst === 'exact' && unref(search) === unref(displayItems)[0]?.title);
      return selectFirst && unref(displayItems).length > 0 && !unref(isPure) && !unref(listHasFocus);
    });

    const menuDisabled = computed(
      () => (props.hideNoData && !unref(items).length) || props.readonly || unref(form?.isReadonly)
    );

    function onClear(event: MouseEvent) {
      if (props.openOnClear) {
        internalMenu.value = true;
      }

      search.value = '';
    }

    function onMousedownControl() {
      if (unref(menuDisabled)) return;
      internalMenu.value = true;
    }

    function onMousedownMenuIcon(event: MouseEvent) {
      if (unref(menuDisabled) || props.disabled) return;

      if (unref(isFocused)) {
        event.preventDefault();
        event.stopPropagation();
      }

      internalMenu.value = !unref(internalMenu);
    }

    function onKeydown(event: KeyboardEvent) {
      if (props.readonly || unref(form?.isReadonly)) return;

      const selectionStart = unref(inputRef)?.selectionStart;
      const length = unref(model).length;

      if (unref(selectionIndex) > -1 || [keyNames.enter, keyNames.down, keyNames.up].includes(event.key)) {
        event.preventDefault();
      }

      if ([keyNames.enter, keyNames.down].includes(event.key)) {
        internalMenu.value = true;
      }

      if ([keyNames.esc].includes(event.key)) {
        internalMenu.value = false;
      }

      if (unref(highlightFirst) && [keyNames.enter, keyNames.tab].includes(event.key)) {
        select(unref(displayItems)[0]);
      }

      if (event.key === keyNames.down) {
        unref(listRef)?.focus('next');
      }

      if (!props.multiple) return;

      if ([keyNames.backspace, keyNames.delete].includes(event.key)) {
        if (unref(selectionIndex) < 0) {
          if (event.key === keyNames.backspace && !unref(search)) {
            selectionIndex.value = length - 1;
          }

          return;
        }

        const originalSelectionIndex = unref(selectionIndex);

        const selectedItem = unref(model)[unref(selectionIndex)];
        if (selectedItem) select(selectedItem);

        selectionIndex.value = originalSelectionIndex >= length - 1 ? length - 2 : originalSelectionIndex;
      }

      if (event.key === keyNames.left) {
        if (unref(selectionIndex) < 0 && selectionStart > 0) return;

        const prev = unref(selectionIndex) > -1 ? unref(selectionIndex) - 1 : length - 1;

        if (unref(model)[prev]) {
          selectionIndex.value = prev;
        } else {
          selectionIndex.value = -1;
          // @todo setSelectionRange is not a function
          // unref(inputRef)?.setSelectionRange(unref(search)?.length, unref(search)?.length);
        }
      }

      if (event.key === keyNames.right) {
        if (unref(selectionIndex) < 0) return;

        const next = unref(selectionIndex) + 1;

        if (unref(model)[next]) {
          selectionIndex.value = next;
        } else {
          selectionIndex.value = -1;
          // @todo setSelectionRange is not a function
          // unref(inputRef)?.setSelectionRange(0, 0);
        }
      }
    }

    function onInput(event: InputEvent) {
      search.value = (event.target as HTMLInputElement).value;
    }

    function onChange(event: Event) {
      if (
        matchesSelector<typeof CdsTextInput>(unref(inputRef), ':autofill') ||
        matchesSelector<typeof CdsTextInput>(unref(inputRef), ':-webkit-autofill')
      ) {
        const item = unref(items).find((item) => item.title === (event.target as HTMLInputElement).value);
        if (item) select(item);
      }
    }

    function onAfterLeave() {
      if (unref(isFocused)) {
        isPure.value = true;
        unref(inputRef)?.focus();
      }
    }

    function onFocusin(event: FocusEvent) {
      isFocused.value = true;
      setTimeout(() => {
        listHasFocus.value = true;
      });
    }
    function onFocusout(event: FocusEvent) {
      listHasFocus.value = false;
    }
    function onUpdateModelValue(value: any) {
      if (value == null || (value === '' && !props.multiple)) model.value = [];
    }

    const isSelecting = shallowRef(false);
    function select(item: IInternalItem) {
      if (item.props.readonly || item.props.disabled) return;

      if (props.multiple) {
        const index = unref(model).findIndex((selection) => deepEqual(selection.value, item.value));

        if (index === -1) {
          model.value = [...unref(model), item];
        } else {
          const value = [...unref(model)];
          value.splice(index, 1);
          model.value = value;
        }
      } else {
        model.value = [item];
        isSelecting.value = true;
        search.value = item.title;
        internalMenu.value = false;
        isPure.value = true;

        nextTick(() => (isSelecting.value = false));
      }
    }

    function selectAll() {
      if (props.readonly || props.disabled) return;

      if (props.multiple) {
        if (!selectedAll.value || selectedIndeterminate.value) {
          model.value = [...items.value];
        } else {
          model.value = [];
        }

        emit('update:selectedAll', unref(selectedAll));
      }
    }

    watch(
      () => props.modelValue,
      (value) => {
        if (!value) {
          search.value = '';
        }
      }
    );

    watch(isFocused, (val, oldValue) => {
      if (val === oldValue) return;

      if (val) {
        isSelecting.value = true;
        search.value = props.multiple ? '' : String(unref(model).at(-1)?.props.title ?? '');
        isPure.value = true;

        nextTick(() => (isSelecting.value = false));
      } else {
        if (!props.multiple && !unref(search)) {
          model.value = [];
        } else if (
          unref(highlightFirst) &&
          !unref(listHasFocus) &&
          !unref(model).some(({ value }) => value === unref(displayItems)[0].value)
        ) {
          select(unref(displayItems)[0]);
        }

        internalMenu.value = false;
        if (!model.value.some(({ title }) => title === search.value)) search.value = '';
        selectionIndex.value = -1;
      }
    });

    watch(search, (value) => {
      if (!unref(isFocused) || unref(isSelecting)) return;

      if (value) internalMenu.value = true;

      isPure.value = !value;
    });

    watch(internalMenu, () => {
      if (!props.hideSelected && unref(internalMenu) && unref(model).length) {
        const index = unref(displayItems).findIndex((item) =>
          unref(model).some((selection) => item.value === selection.value)
        );
        IN_BROWSER &&
          window.requestAnimationFrame(() => {
            // @todo VirtualScroll??
          });
      }
    });

    const { inputProps } = useFilteredSelectProps(props, CdsTextInput.props, []);
    const hasNoDataItem = computed(() => !unref(displayItems).length && !props.hideNoData);

    useRender(() => {
      const hasList =
        !!(!props.hideNoData || unref(displayItems).length) ||
        slots['prepend-item'] ||
        slots['append-item'] ||
        slots['no-data'];
      const isDirty = unref(model).length > 0;

      return h(
        CdsTextInput,
        {
          ref: inputRef,
          ...unref(inputProps),
          modelValue: unref(search),
          'onUpdate:modelValue': onUpdateModelValue,
          focused: unref(isFocused),
          'onUpdate:focused': (value) => (isFocused.value = value),
          validationValue: model.externalValue,
          dirty: isDirty,
          persistentClearable: isDirty,
          class: classNames('cds-autocomplete', `cds-autocomplete--${props.multiple ? 'multiple' : 'single'}`, {
            'cds-autocomplete--ellipsis': props.ellipsis,
            'cds-autocomplete--disabled': props.disabled,
            'cds-autocomplete--selected': unref(model).length,
            'cds-autocomplete--active-menu': unref(internalMenu),
            'cds-autocomplete--selection-slot': !!slots.selection,
            'cds-autocomplete--selecting-index': unref(selectionIndex) > -1
          }),
          onInput,
          onChange,
          onKeydown,
          'onClick:clear': onClear,
          'onMousedown:control': onMousedownControl
        },
        {
          ...slots,
          default: () =>
            h(Fragment, {}, [
              h(
                CdsMenu,
                {
                  ref: menuRef,
                  modelValue: unref(internalMenu),
                  'onUpdate:modelValue': (value: boolean) => (internalMenu.value = value),
                  activator: 'parent',
                  maxHeight: props.menuHeight,
                  contentClasses: 'cds-autocomplete__content',
                  disabled: unref(menuDisabled),
                  eager: props.eager,
                  openOnClick: false,
                  closeOnContentClick: false,
                  transition: props.transition,
                  onAfterLeave,
                  ...props.menuProps
                },
                {
                  default: () =>
                    hasList &&
                    h(
                      CdsList,
                      {
                        ref: listRef,
                        selected: unref(selected),
                        selectStrategy: unref(selectStrategy),
                        tabindex: '-1',
                        onMousedown: (event: MouseEvent) => event.preventDefault(),
                        onKeydown: onListKeydown,
                        onFocusin,
                        onFocusout,
                        onScrollPassive: onListScroll,
                        ...props.listProps
                      },
                      {
                        default: () =>
                          h(Fragment, {}, [
                            slots['prepend-item']?.() ??
                              (props.multiple &&
                                props.isSelectAll && [
                                  h(
                                    CdsListItem,
                                    {
                                      isClickable: true,
                                      title: autocompleteLocale.selectAll,
                                      class: 'cds-autocomplete__select-all',
                                      onClick: selectAll
                                    },
                                    {
                                      prepend: () =>
                                        h(CdsCheckbox, {
                                          modelValue: unref(selectedAll),
                                          indeterminate: unref(selectedIndeterminate),
                                          hideMessages: true,
                                          tabindex: '-1'
                                        })
                                    }
                                  ),
                                  h(CdsDivider)
                                ]),
                            unref(hasNoDataItem) &&
                              (slots['no-data']?.() ?? h(CdsListItem, { title: autocompleteLocale.noData })),
                            unref(displayItems).map((item, index) => {
                              const itemProps = mergeProps(item.props, {
                                key: index,
                                active: unref(highlightFirst) && index === 0 ? true : undefined,
                                onClick: () => select(item)
                              });

                              return (
                                slots.item?.({
                                  item,
                                  index,
                                  props: itemProps
                                }) ??
                                h(
                                  CdsListItem,
                                  { ...itemProps },
                                  {
                                    prepend:
                                      (props.multiple && !props.hideSelected) || item.props.prependIcon
                                        ? ({ isSelected }: IItemSlotProps) =>
                                            h(Fragment, {}, [
                                              props.multiple && !props.hideSelected
                                                ? h(CdsCheckbox, {
                                                    key: item.value,
                                                    modelValue: unref(isSelected),
                                                    hideMessages: true,
                                                    tabindex: '-1'
                                                  })
                                                : undefined,
                                              item.props.prependIcon && h(CdsIcon, { name: item.props.prependIcon })
                                            ])
                                        : undefined,
                                    title: () =>
                                      CdsHighlight({
                                        name: 'autocomplete',
                                        isPure: unref(isPure),
                                        item,
                                        matches: getMatches(item)?.title,
                                        length: unref(search)?.length ?? 0
                                      })
                                  }
                                )
                              );
                            }),
                            slots['append-item']?.()
                          ])
                      }
                    )
                }
              ),
              unref(model).map((item, index) => {
                const slotProps = {};

                return h(
                  'div',
                  {
                    key: item.value,
                    class: classNames('cds-autocomplete__selection', {
                      'cds-autocomplete__selection--selected': index === unref(selectionIndex)
                    })
                  },
                  slots.selection?.({ item, index }) ??
                    h('span', {}, [
                      item.title,
                      props.multiple &&
                        index < unref(model).length - 1 &&
                        h('span', { class: 'cds-autocomplete__selection--comma' }, ',')
                    ])
                );
              })
            ]),
          'append-inner': () => [
            slots['append-inner']?.(),
            unref(hasList) &&
              h(CdsIcon, {
                name: props.menuIcon,
                onClick: noop,
                class: 'cds-autocomplete__menu-icon',
                onMousedown: onMousedownMenuIcon
              })
          ]
        }
      );
    });

    expose({
      isFocused,
      isPure,
      menu: internalMenu,
      filteredItems,
      select,
      selectAll,
      menuRef,
      listRef,
      textInputRef: inputRef
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsAutocomplete';
</style>
